'use client';
import { createContext, useContext, useState } from 'react';
import NotificationToast from '../_components/notifications/NotificationToast';

export type ToastKind = 'info' | 'error' | 'success';

export type NotificationToastType = {
  key: string;
  message: string;
  kind: ToastKind;
  show?: boolean;
  onDismiss?: () => void;
};

type ContextNotificationToastType = {
  toasts: Array<NotificationToastType>;
  setToast: (toastDetails: NotificationToastType) => void;
  clearToast: (key: string) => void;
};

const NotificationContext = createContext<ContextNotificationToastType>({
  toasts: [],
  setToast: () => {},
  clearToast: (key: string) => {},
});

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const TOAST_TRANSITION_SLEEP = 800;

function NotificationContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Array<NotificationToastType>>([]);

  const setToast = async (toastDetails: NotificationToastType) => {
    // workaround to keep the transition effect
    setToasts((prevState: Array<NotificationToastType>) => {
      const existingToast = prevState.find((n) => n.key === toastDetails.key);

      if (!existingToast) {
        prevState.push({
          ...toastDetails,
          show: false,
        });
      }

      return [...prevState];
    });
    await sleep(TOAST_TRANSITION_SLEEP);
    // workaround to keep the transition effect

    setToasts((prevState: Array<NotificationToastType>) => {
      prevState = prevState.map((item) => {
        return { ...item, show: true };
      });
      return [...prevState];
    });
  };

  const clearToast = async (key: string) => {
    const index = toasts.findIndex((t) => t.key === key);
    index > -1 && handleCloseToast(index);
  };

  const handleCloseToast = async (index: number) => {
    // workaround to keep the transition effect
    setToasts((prevState: Array<NotificationToastType>) => {
      prevState = prevState.map((item, prevIndex) => {
        if (prevIndex === index) {
          return { ...item, show: false };
        }
        return item;
      });
      return [...prevState];
    });
    await sleep(TOAST_TRANSITION_SLEEP);
    // workaround to keep the transition effect

    setToasts((prevState: Array<NotificationToastType>) => {
      prevState.splice(index, 1);
      return [...prevState];
    });
  };

  return (
    <NotificationContext.Provider
      value={{ toasts: toasts, setToast, clearToast }}
    >
      {toasts.length > 0 && (
        <div
          aria-live="assertive"
          className="pointer-events-none z-[100] fixed top-[30px] right-0 md:right-[20px]"
        >
          {toasts.map((toast: NotificationToastType, index: number) => {
            return (
              <NotificationToast
                show={toast.show}
                handleClose={() => {
                  handleCloseToast(index);
                  !!toast.onDismiss && toast.onDismiss();
                }}
                message={toast.message}
                key={`toast-${index}`}
                kind={toast.kind}
              />
            );
          })}
        </div>
      )}
      {children}
    </NotificationContext.Provider>
  );
}

const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      `useNotificationContext must be used within a NotificationContextProvider`
    );
  }
  return context;
};

export {
  useNotificationContext,
  NotificationContext,
  NotificationContextProvider,
  TOAST_TRANSITION_SLEEP,
};
